import * as React from 'react';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { authFetch } from '../authFetch';

export default function FeedbackTab() {
  const [feedbackType, setFeedbackType] = useState('suggestion');
  const [message, setMessage] = useState('');
  const [attachScreenshot, setAttachScreenshot] = useState(false);
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    client: ''
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await authFetch('/api/user/profile/');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setUserInfo(data);
      } catch (error) {
        console.error("Error fetching user info:", error);
        setSnackbar({ open: true, message: 'Could not load user information.', severity: 'error' });
      }
    };
    fetchUserInfo();
  }, []);

  const handleFeedbackTypeChange = (event) => {
    setFeedbackType(event.target.value);
  };

  const handleMessageChange = (event) => {
    setMessage(event.target.value);
  };

  const handleAttachScreenshotChange = (event) => {
    setAttachScreenshot(event.target.checked);
    if (!event.target.checked) {
      setScreenshotFile(null);
    }
  };

  const handleFileChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      setScreenshotFile(event.target.files[0]);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append('feedback_type', feedbackType);
    formData.append('message', message);
    formData.append('user', JSON.stringify(userInfo));
    if (attachScreenshot && screenshotFile) {
      formData.append('screenshot', screenshotFile);
    }

    try {
      // --- THIS IS THE CORRECTED PART ---
      // We pass a special header option to tell authFetch to let the browser
      // handle the Content-Type for file uploads.
      const response = await authFetch('/api/submit-feedback/', {
        method: 'POST',
        body: formData,
        // By setting Content-Type to null or undefined, we prevent our
        // authFetch helper from setting it to 'application/json'.
        // The browser will then correctly set it to 'multipart/form-data'
        // with the required boundary.
        headers: {
          'Content-Type': null, 
        },
      });
      // --- END OF CORRECTION ---

      if (!response.ok) {
        // This will now hopefully give a more specific error from the backend
        const errorData = await response.json().catch(() => ({})); // try to get error details
        console.error("Submission failed with status:", response.status, errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      setSnackbar({ open: true, message: responseData.message || 'Feedback submitted successfully!', severity: 'success' });
      setMessage('');
      setFeedbackType('suggestion');
      setAttachScreenshot(false);
      setScreenshotFile(null);

    } catch (error) {
      console.error("Error submitting feedback:", error);
      setSnackbar({ open: true, message: 'Failed to submit feedback.', severity: 'error' });
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: { sm: '100%', md: '800px' },
        mx: 'auto',
      }}
    >
      <Typography component="h1" variant="h6" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'left' }}>
        Submit Feedback
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, textAlign: 'left' }}>
        We appreciate your feedback. Please let us know how we can improve.
      </Typography>
      
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
        
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="feedback-type-label">Feedback Type</InputLabel>
          <Select
            labelId="feedback-type-label"
            id="feedback-type-select"
            value={feedbackType}
            label="Feedback Type" // This will now work correctly
            onChange={handleFeedbackTypeChange}
          >
            <MenuItem value="suggestion">Suggestion for Improvement</MenuItem>
            <MenuItem value="new_feature">New Feature Request</MenuItem>
            <MenuItem value="bug_report">Bug Report</MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          id="feedback-description"
          label="Description" // This will now work correctly
          variant="outlined"
          placeholder="Type your feedback here..."
          multiline
          rows={12}
          fullWidth
          required
          value={message}
          onChange={handleMessageChange}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={attachScreenshot}
              onChange={handleAttachScreenshotChange}
              name="attachScreenshot"
            />
          }
          label="Attach Screenshot"
          sx={{ display: 'block', textAlign: 'left' }}
        />

        {attachScreenshot && (
          <Box sx={{ my: 2, display: 'flex', alignItems: 'center' }}>
            <Button variant="outlined" component="label">
              Choose File
              <input type="file" hidden accept="image/*" onChange={handleFileChange} />
            </Button>
            {screenshotFile && (
              <Typography variant="body2" sx={{ ml: 2 }}>
                {screenshotFile.name}
              </Typography>
            )}
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 2, mb: 2 }}
        >
          Submit Feedback
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}